import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { entitiesApi } from '@/lib/entities';
import type { FxRateKind } from '@/lib/fxRates';
import { consolidationApi } from '@/lib/consolidation';
import { formatMoney } from '@/lib/currency';
import { CurrencyPicker } from '@/components/CurrencyPicker';
import { EntityPicker } from '@/components/EntityPicker';

/**
 * Workspace-level consolidation surface. Shown on the dashboard when
 * the workspace has more than one entity (otherwise it's just the
 * primary entity and the per-entity view IS the consolidation).
 *
 * Submits a single GET /api/workspaces/:wsId/consolidate per click.
 * Caching off — consolidation is a derived view, not a stored snapshot.
 */
export function ConsolidationPanel({ workspaceId }: { workspaceId: string }) {
  const { t } = useTranslation();

  const { data: entities = [] } = useQuery({
    queryKey: ['entities', workspaceId],
    queryFn: () => entitiesApi.list(workspaceId),
  });
  const primary = useMemo(() => entities.find((e) => e.isPrimary), [entities]);

  // Sensible defaults: prior calendar month, reporting in the primary's currency.
  const today = new Date();
  const firstOfPrev = new Date(today.getFullYear(), today.getMonth() - 1, 1).toISOString().slice(0, 10);
  const lastOfPrev  = new Date(today.getFullYear(), today.getMonth(), 0).toISOString().slice(0, 10);

  const [periodStart, setPeriodStart] = useState(firstOfPrev);
  const [periodEnd, setPeriodEnd] = useState(lastOfPrev);
  const [reportingCurrency, setReportingCurrency] = useState(primary?.currency ?? 'USD');
  const [rootEntityId, setRootEntityId] = useState<string>('');
  const [rateKind, setRateKind] = useState<FxRateKind>('period_end');
  // Part 11
  const [mode, setMode] = useState<'pl' | 'balance_sheet'>('pl');
  const [perAccountRateKinds, setPerAccountRateKinds] = useState(false);
  const [icPairMatch, setIcPairMatch] = useState(false);

  // Bump default reporting currency once the primary entity loads, but
  // don't stomp on a user choice.
  if (primary && reportingCurrency === 'USD' && primary.currency !== 'USD') {
    setReportingCurrency(primary.currency);
  }

  const run = useMutation({
    mutationFn: () => consolidationApi.run(workspaceId, {
      periodStart,
      periodEnd,
      reportingCurrency: reportingCurrency.toUpperCase(),
      rootEntityId: rootEntityId || undefined,
      rateKind,
      mode,
      perAccountRateKinds,
      icPairMatch,
    }),
  });

  // No point showing the panel if there's only the primary entity.
  if (entities.length <= 1) return null;

  return (
    <div className="rounded-md border border-border bg-card p-5 space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end">
        <label className="block">
          <span className="block text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-medium mb-1">{t('forecast.hist_start')}</span>
          <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)}
            className="num w-full rounded-md border border-border bg-white px-2 py-1.5 text-xs" />
        </label>
        <label className="block">
          <span className="block text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-medium mb-1">{t('forecast.hist_end')}</span>
          <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)}
            className="num w-full rounded-md border border-border bg-white px-2 py-1.5 text-xs" />
        </label>
        <label className="block">
          <span className="block text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-medium mb-1">{t('consolidation.reporting_currency')}</span>
          <CurrencyPicker value={reportingCurrency} onChange={setReportingCurrency}
            ariaLabel={t('consolidation.reporting_currency')}
            className="num w-full rounded-md border border-border bg-white px-2 py-1.5 text-xs" />
        </label>
        <label className="block">
          <span className="block text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-medium mb-1">{t('consolidation.scope')}</span>
          <EntityPicker
            wsId={workspaceId}
            value={rootEntityId}
            onChange={setRootEntityId}
            includeNone
            noneLabel={t('consolidation.scope_group')}
            className="w-full rounded-md border border-border bg-white px-2 py-1.5 text-xs"
          />
        </label>
        <label className="block">
          <span className="block text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-medium mb-1">{t('fx.kind')}</span>
          <select value={rateKind} onChange={(e) => setRateKind(e.target.value as FxRateKind)}
            className="w-full rounded-md border border-border bg-white px-2 py-1.5 text-xs">
            <option value="period_end">{t('fx.kind_end')}</option>
            <option value="avg">{t('fx.kind_avg')}</option>
            <option value="historical">{t('fx.kind_historical')}</option>
          </select>
        </label>
      </div>
      {/* Part 11 — global consolidation options */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
        <label className="inline-flex items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-medium">{t('consolidation.mode', { defaultValue: 'Statement' })}</span>
          <select value={mode} onChange={(e) => setMode(e.target.value as 'pl' | 'balance_sheet')} className="rounded-md border border-border bg-white px-2 py-1 text-xs">
            <option value="pl">{t('consolidation.mode_pl', { defaultValue: 'P&L' })}</option>
            <option value="balance_sheet">{t('consolidation.mode_bs', { defaultValue: 'Balance sheet (CTA)' })}</option>
          </select>
        </label>
        <label className="inline-flex items-center gap-1.5 text-ink-700">
          <input type="checkbox" checked={perAccountRateKinds} onChange={(e) => setPerAccountRateKinds(e.target.checked)} className="rounded border-border" />
          {t('consolidation.per_account_rates', { defaultValue: 'Per-account rate kinds' })}
        </label>
        <label className="inline-flex items-center gap-1.5 text-ink-700">
          <input type="checkbox" checked={icPairMatch} onChange={(e) => setIcPairMatch(e.target.checked)} className="rounded border-border" />
          {t('consolidation.ic_pair_match', { defaultValue: 'Eliminate intercompany (pairs)' })}
        </label>
      </div>
      <div className="flex justify-end">
        <button onClick={() => run.mutate()} disabled={run.isPending}
          className="h-9 px-3.5 rounded-md bg-foreground text-background text-[12.5px] font-semibold hover:bg-foreground/90 disabled:opacity-60">
          {run.isPending ? t('consolidation.running') : t('consolidation.run')}
        </button>
      </div>

      {run.error && (
        <div className="rounded-md bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2 text-xs">
          {(run.error as Error).message}
        </div>
      )}

      {run.data && (
        <div className="space-y-3 pt-2 border-t border-border">
          {run.data.missingRates.length > 0 && (
            <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 text-xs">
              {t('consolidation.missing_rates_warn', {
                pairs: run.data.missingRates.map((p) => `${p.from}→${p.to}`).join(', '),
              })}
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {run.data.missingRates.map((p) => (
                  <Link
                    key={`${p.from}-${p.to}`}
                    to={`/app/workspace/${workspaceId}/fx-rates?base=${p.from}&quote=${p.to}`}
                    className="num text-[11px] px-1.5 py-0.5 rounded border border-amber-300 text-amber-800 hover:bg-amber-100"
                  >
                    + {p.from}→{p.to}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {run.data.icMatched !== undefined && run.data.icMatched > 0 && (
            <div className="rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-2 text-xs">
              {t('consolidation.ic_matched', { defaultValue: `Intercompany eliminated: ${formatMoney(run.data.icMatched, { currency: run.data.reportingCurrency, maximumFractionDigits: 0 })}`, amount: formatMoney(run.data.icMatched, { currency: run.data.reportingCurrency, maximumFractionDigits: 0 }) })}
              {run.data.icResidual ? <span className="ml-2 text-amber-700">{t('consolidation.ic_residual', { defaultValue: `residual ${formatMoney(run.data.icResidual, { currency: run.data.reportingCurrency, maximumFractionDigits: 0 })}`, amount: formatMoney(run.data.icResidual, { currency: run.data.reportingCurrency, maximumFractionDigits: 0 }) })}</span> : null}
            </div>
          )}
          {run.data.mode === 'balance_sheet' && run.data.cta && !run.data.cta.available && (
            <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 text-xs">{run.data.cta.reason}</div>
          )}

          <div className="flex items-baseline justify-between gap-3">
            <span className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-medium">
              {t('consolidation.entities_contributing', { count: run.data.entityIds.length })}
            </span>
            <span className="num text-xl font-semibold text-foreground tabular-nums">
              {t('consolidation.total')}:{' '}
              {formatMoney(run.data.totalAmount, { currency: run.data.reportingCurrency, maximumFractionDigits: 0 })}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[480px]">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="py-2 pr-3 font-medium text-[10px] uppercase tracking-[0.16em]">{t('variance.th_account')}</th>
                  <th className="py-2 px-3 font-medium text-[10px] uppercase tracking-[0.16em] text-right">{t('consolidation.total')}</th>
                  <th className="py-2 px-3 font-medium text-[10px] uppercase tracking-[0.16em] text-right">{t('entities.title')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {run.data.lines.map((l) => {
                  const isCta = l.accountCode === '__CTA__';
                  return (
                    <tr key={l.accountCode} className={isCta ? 'bg-violet-50/60' : 'hover:bg-foreground/[0.02]'}>
                      <td className="py-2 pr-3 num font-medium text-foreground">
                        {isCta ? t('consolidation.cta_label', { defaultValue: 'CTA (translation adjustment)' }) : l.accountCode}
                        {l.rateKind && !isCta && <span className="ml-1.5 text-[10px] uppercase tracking-wide rounded px-1 py-0.5 bg-ink-100 text-ink-500">{l.rateKind}</span>}
                      </td>
                      <td className="py-2 px-3 num text-right tabular-nums text-foreground">
                        {formatMoney(l.amount, { currency: run.data!.reportingCurrency, maximumFractionDigits: 0 })}
                      </td>
                      <td className="py-2 px-3 num text-right text-muted-foreground">{isCta ? '—' : l.entityCount}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
